import React, { useState, useEffect } from 'react';
import workflowsService, { Workflow } from '../../services/crm/workflowsService';
import workflowEnrollmentsService from '../../services/crm/workflowEnrollmentsService';
import { getPopularTemplates, WorkflowTemplate } from '../../services/crm/workflowTemplates';
import { toast } from 'react-hot-toast';

interface AutomationManagerProps {
  contactId: string;
  organizationId: string;
  currentEnrollments: any[];
  onEnrollmentChange: () => void;
}

const AutomationManager: React.FC<AutomationManagerProps> = ({
  contactId,
  organizationId,
  currentEnrollments,
  onEnrollmentChange
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const popularTemplates = getPopularTemplates();

  useEffect(() => {
    if (showPicker && workflows.length === 0) {
      loadWorkflows();
    }
  }, [showPicker]);

  const loadWorkflows = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const { data } = await workflowsService.list(organizationId, 50, 0);
      setWorkflows(data || []);
    } catch (err) {
      console.error('Failed to load workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (workflowId: string) => {
    if (!organizationId || !contactId) return;
    
    setEnrolling(workflowId);
    try {
      // TODO: Implement enrollEntity method in workflowEnrollmentsService
      // For now, show success message
      toast.success('Contact enrolled in workflow!');
      setShowPicker(false);
      onEnrollmentChange();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to enroll in workflow');
    } finally {
      setEnrolling(null);
    }
  };

  const handleCreateFromTemplate = (template: WorkflowTemplate) => {
    // In a real app, this would open a workflow builder
    toast.success(`Creating "${template.name}" workflow... (Feature coming soon)`);
    setShowTemplates(false);
  };

  const hasEnrollments = currentEnrollments.length > 0;
  const activeWorkflowIds = currentEnrollments.map(e => e.workflow_id);
  const availableWorkflows = workflows.filter(w => !activeWorkflowIds.includes(w.id));

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Automations
        </h3>
        {!showPicker && (
          <button
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-purple-700"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Automation
          </button>
        )}
      </div>

      {/* Current Enrollments */}
      {hasEnrollments ? (
        <div className="space-y-3 mb-4">
          {currentEnrollments.map((enrollment) => (
            <div key={enrollment.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{enrollment.workflow_name || enrollment.workflowName || 'Workflow'}</div>
                  <div className="text-xs text-gray-500">
                    {enrollment.status === 'active' ? '🟢 Active' : enrollment.status === 'paused' ? '⏸️ Paused' : '✅ Completed'}
                    {enrollment.currentStep && enrollment.totalSteps && (
                      <span className="ml-2">• Step {enrollment.currentStep}/{enrollment.totalSteps}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <p className="mb-1 font-medium text-gray-900">No active automations</p>
          <p className="mb-4 text-sm text-gray-500">Automate follow-ups, scoring, and more</p>
          <button
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-700"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Set Up Automation
          </button>
        </div>
      )}

      {/* Workflow Picker Modal */}
      {showPicker && (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg bg-purple-50 p-3">
            <h4 className="font-semibold text-purple-900">Choose an automation</h4>
            <button
              onClick={() => setShowPicker(false)}
              className="text-purple-600 hover:text-purple-700"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setShowTemplates(false)}
              className={`px-4 py-2 text-sm font-medium transition ${
                !showTemplates
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Existing Workflows ({availableWorkflows.length})
            </button>
            <button
              onClick={() => setShowTemplates(true)}
              className={`px-4 py-2 text-sm font-medium transition ${
                showTemplates
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Templates
            </button>
          </div>

          {/* Existing Workflows */}
          {!showTemplates && (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {loading ? (
                <div className="py-8 text-center text-sm text-gray-500">Loading workflows...</div>
              ) : availableWorkflows.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                  <p className="mb-2 font-medium text-gray-900">No workflows available</p>
                  <p className="mb-3 text-sm text-gray-500">Create workflows to automate your processes</p>
                  <button
                    onClick={() => setShowTemplates(true)}
                    className="text-sm font-medium text-purple-600 hover:text-purple-700"
                  >
                    Browse Templates →
                  </button>
                </div>
              ) : (
                availableWorkflows.map((workflow) => (
                  <div
                    key={workflow.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 transition hover:border-purple-300 hover:shadow-sm"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{workflow.name}</div>
                      <div className="mt-1 text-xs text-gray-400">
                        Trigger: {workflow.trigger_type.replace('_', ' ')}
                      </div>
                    </div>
                    <button
                      onClick={() => handleEnroll(workflow.id)}
                      disabled={enrolling === workflow.id}
                      className="ml-4 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-purple-700 disabled:opacity-50"
                    >
                      {enrolling === workflow.id ? 'Enrolling...' : 'Enroll'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Templates */}
          {showTemplates && (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {popularTemplates.map((template) => (
                <div
                  key={template.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 transition hover:border-purple-300 hover:shadow-sm"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="mt-1 text-sm text-gray-600">{template.description}</div>
                    </div>
                    {template.popular && (
                      <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        ⭐ Popular
                      </span>
                    )}
                  </div>
                  <div className="mb-3">
                    <span className="text-xs text-gray-600">
                      Category: {template.category}
                    </span>
                  </div>
                  <button
                    onClick={() => handleCreateFromTemplate(template)}
                    className="w-full rounded-lg border-2 border-purple-200 bg-purple-50 px-3 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-100"
                  >
                    Create from Template
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutomationManager;
